import { useState, useMemo } from 'react'
import { PageHeader } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Plus, TrendingUp } from 'lucide-react'
import { Link } from 'react-router-dom'
import { ServiciosTable } from '@/components/servicios/ServiciosTable'
import { ServicioFormDialog } from '@/components/servicios/ServicioFormDialog'
import { ServicioDetalleDialog } from '@/components/servicios/ServicioDetalleDialog'
import { ServiciosFiltros } from '@/components/servicios/ServiciosFiltros'
import { useServiciosFiltrosURL } from '@/components/servicios/useServiciosFiltrosURL'
import { type ServicioFormValues, precioEditable } from '@/components/servicios/forms'
import {
  useServicios,
  useCrearServicio,
  useActualizarServicio,
  useEliminarServicio,
  useClientesDelServicio,
} from '@/hooks/useServicios'
import type { Servicio } from '@/types/database'
import type { ServicioConClientes } from '@/services/serviciosService'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { usePermissions } from '@/hooks/usePermissions'
import { toast } from 'sonner'
import { handleMutationError } from '@/lib/permisos-guard'

export function Servicios() {
  const { puede, permisosListos } = usePermissions()
  // Filtros sincronizados con URL
  const { filtros, setFiltros } = useServiciosFiltrosURL()

  // Estado para dialogs
  const [dialogOpen, setDialogOpen] = useState(false)
  const [servicioEditar, setServicioEditar] = useState<Servicio | null>(null)
  const [servicioEliminar, setServicioEliminar] = useState<ServicioConClientes | null>(null)
  const [servicioVerClientes, setServicioVerClientes] = useState<ServicioConClientes | null>(null)
  const [servicioDetalle, setServicioDetalle] = useState<ServicioConClientes | null>(null)

  // Queries y mutations
  // isPending y no isLoading: mientras la query espera a los permisos no corre, y una lista vacía
  // en ese momento no quiere decir que no haya servicios.
  const { data: servicios = [], isPending: isPendingServicios, error, refetch } = useServicios()
  const { data: clientesDelServicio = [] } = useClientesDelServicio(servicioVerClientes?.id || '')
  const crearServicio = useCrearServicio()
  const actualizarServicio = useActualizarServicio()
  const eliminarServicio = useEliminarServicio()

  // Handlers
  const handleNuevoServicio = () => {
    setServicioEditar(null)
    setDialogOpen(true)
  }

  const handleEditarServicio = (servicio: ServicioConClientes) => {
    setServicioEditar(servicio)
    setDialogOpen(true)
  }

  const handleEliminarServicio = (servicio: ServicioConClientes) => {
    if (servicio.clientesCount > 0) {
      toast.error('No se puede eliminar un servicio con clientes asignados')
      return
    }
    setServicioEliminar(servicio)
  }

  // La lista de clientes del servicio trae el precio de cada uno.
  const handleVerClientes = (servicio: ServicioConClientes) => {
    if (!puede.verHonorariosCliente) return
    setServicioVerClientes(servicio)
  }

  const handleVerDetalle = (servicio: ServicioConClientes) => {
    setServicioDetalle(servicio)
  }

  const handleEditarDesdeDetalle = (servicio: ServicioConClientes) => {
    setServicioDetalle(null)
    setServicioEditar(servicio)
    setDialogOpen(true)
  }

  const handleSubmit = async (data: ServicioFormValues) => {
    try {
      const servicioData = {
        nombre: data.nombre,
        descripcion: data.descripcion || null,
        // Si el formulario no leyó el precio, mandarlo pisaría el real con null.
        ...(precioEditable(puede.verHonorariosCliente, servicioEditar) && { precio_estimado: data.precio_estimado || null }),
        categoria: data.categoria,
        estado: data.estado,
        periodicidad: data.periodicidad,
        aplica_persona_fisica: data.aplica_persona_fisica,
        aplica_persona_juridica: data.aplica_persona_juridica,
        aplica_sucesion_indivisa: data.aplica_sucesion_indivisa,
      }

      if (servicioEditar) {
        await actualizarServicio.mutateAsync({
          id: servicioEditar.id,
          data: servicioData,
        })
        toast.success('Servicio actualizado correctamente')
      } else {
        await crearServicio.mutateAsync(servicioData)
        toast.success('Servicio creado correctamente')
      }
      setDialogOpen(false)
      setServicioEditar(null)
    } catch (err) {
      toast.error(
        servicioEditar
          ? 'Error al actualizar el servicio'
          : 'Error al crear el servicio'
      )
      console.error(err)
    }
  }

  const handleConfirmarEliminar = async () => {
    if (!servicioEliminar) return
    try {
      await eliminarServicio.mutateAsync(servicioEliminar.id)
      toast.success('Servicio eliminado correctamente')
      setServicioEliminar(null)
    } catch (err) {
      handleMutationError(err, 'Error al eliminar el servicio')
    }
  }

  // ORR-204: si el rol no ve el catálogo completo (asistente), restringir la base a los
  // servicios que usan sus clientes delegados. clientesCount ya viene scopeado por la RLS
  // de cliente_servicio_precios (Fase 3 Tanda A), así que basta con clientesCount > 0.
  const serviciosBase = useMemo(
    () => (puede.verCatalogoServicios ? servicios : servicios.filter((s) => s.clientesCount > 0)),
    [servicios, puede.verCatalogoServicios]
  )

  // Filtrar servicios
  const serviciosFiltrados = useMemo(() => {
    return serviciosBase.filter((servicio) => {
      // Acceder a propiedades extendidas del servicio
      const servicioExt = servicio as ServicioConClientes & {
        descripcion?: string | null
        estado?: string | null
        categoria?: string | null
      }

      // Filtro por búsqueda
      if (filtros.busqueda) {
        const termino = filtros.busqueda.toLowerCase()
        const coincide =
          servicio.nombre?.toLowerCase().includes(termino) ||
          servicioExt.descripcion?.toLowerCase().includes(termino)
        if (!coincide) return false
      }

      // Filtro por estado
      if (filtros.estado !== 'todos' && servicioExt.estado !== filtros.estado) {
        return false
      }

      // Filtro por categoría
      if (filtros.categoria !== 'todos' && servicioExt.categoria !== filtros.categoria) {
        return false
      }

      return true
    })
  }, [serviciosBase, filtros])

  // Estadísticas (sobre datos filtrados)
  const totalServicios = serviciosFiltrados.length
  const serviciosConClientes = serviciosFiltrados.filter(s => s.clientesCount > 0).length
  const totalAsignaciones = serviciosFiltrados.reduce((acc, s) => acc + s.clientesCount, 0)

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Servicios"
        description="Gestiona los servicios contables que ofreces"
      />

      <main className="flex-1 p-6 space-y-6">
        {/* Barra de filtros y acciones */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-start">
          <ServiciosFiltros
            filtros={filtros}
            onChange={setFiltros}
            onRefresh={() => refetch()}
            isRefreshing={isPendingServicios || !permisosListos}
            className="flex-1"
          />
          <div className="flex gap-2 shrink-0">
            {puede.editarHonorariosCliente && (
              <Button variant="outline" asChild>
                <Link to="/servicios/aumento-inflacion">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Aumento por inflación
                </Link>
              </Button>
            )}
            {puede.editarCatalogoServicios && (
              <Button onClick={handleNuevoServicio}>
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Servicio
              </Button>
            )}
          </div>
        </div>

        {/* Resumen */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-lg border bg-card p-4">
            <p className="text-sm text-muted-foreground">Total Servicios</p>
            <p className="text-2xl font-bold">{totalServicios}</p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <p className="text-sm text-muted-foreground">Con Clientes</p>
            <p className="text-2xl font-bold text-green-600">{serviciosConClientes}</p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <p className="text-sm text-muted-foreground">Asignaciones</p>
            <p className="text-2xl font-bold text-blue-600">{totalAsignaciones}</p>
          </div>
        </div>

        {/* Tabla de servicios */}
        {error ? (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
            <p className="text-destructive">
              Error al cargar los servicios. Verifica la conexión con Supabase.
            </p>
          </div>
        ) : (
          <ServiciosTable
            servicios={serviciosFiltrados}
            isLoading={isPendingServicios}
            onEditar={handleEditarServicio}
            onEliminar={handleEliminarServicio}
            onVerClientes={handleVerClientes}
            onVerDetalle={handleVerDetalle}
            canEdit={puede.editarCatalogoServicios}
            canDelete={puede.eliminarServicio}
          />
        )}

        {/* Resumen de resultados */}
        {!isPendingServicios && !error && (
          <div className="text-sm text-muted-foreground">
            Mostrando {serviciosFiltrados.length} de {serviciosBase.length} servicios
          </div>
        )}
      </main>

      {/* Dialog para crear/editar servicio */}
      <ServicioFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        servicio={servicioEditar}
        onSubmit={handleSubmit}
        isLoading={crearServicio.isPending || actualizarServicio.isPending}
      />

      {/* Dialog de confirmación para eliminar */}
      <AlertDialog
        open={!!servicioEliminar}
        onOpenChange={(open) => !open && setServicioEliminar(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar servicio?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente el
              servicio <strong>{servicioEliminar?.nombre}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmarEliminar}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {eliminarServicio.isPending ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog para ver clientes del servicio */}
      <Dialog
        open={!!servicioVerClientes}
        onOpenChange={(open) => !open && setServicioVerClientes(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Clientes con {servicioVerClientes?.nombre}
            </DialogTitle>
            <DialogDescription>
              {servicioVerClientes?.clientesCount} cliente(s) tienen este servicio asignado
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[300px]">
            <div className="space-y-2">
              {clientesDelServicio.map((cliente) => (
                <div
                  key={cliente.id}
                  className="flex items-center gap-3 p-2 rounded hover:bg-muted"
                >
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-medium text-primary">
                      {cliente.nombre.charAt(0)}
                    </span>
                  </div>
                  <span className="text-sm">{cliente.nombre}</span>
                </div>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Dialog de detalle del servicio */}
      <ServicioDetalleDialog
        servicio={servicioDetalle}
        open={!!servicioDetalle}
        onOpenChange={(open) => !open && setServicioDetalle(null)}
        onEditar={handleEditarDesdeDetalle}
      />
    </div>
  )
}
